import { z } from "zod";
import { opaqueIdSchema } from "./opaque.js";
export const narrativeBookProductSchema = z.enum(["MY_STORY", "OUR_STORY"]);
export const narrativeBookProductionSpecificationSchema = z
    .object({
    contract: z.literal("narrative-book-production/1"),
    product: narrativeBookProductSchema,
    edition: z.literal("CUSTOMER_BOOK_PROOF"),
    format: z.enum(["trade-color-6x9", "trade-color-8.5x8.5"]),
    trim: z
        .object({ widthPoints: z.number().positive(), heightPoints: z.number().positive() })
        .strict(),
    body: z
        .object({
        fontFamily: z.enum(["Literata", "Newsreader"]),
        fontSizePoints: z.number().min(10.5).max(14),
        leadingPoints: z.number().min(14).max(22),
        measurePoints: z.number().min(280).max(420),
        targetCharactersPerLine: z
            .object({ minimum: z.number().int().min(45), maximum: z.number().int().max(75) })
            .strict(),
    })
        .strict(),
    contentTarget: z
        .object({
        minimumWords: z.number().int().positive(),
        preferredWords: z.number().int().positive(),
        minimumInteriorPages: z.number().int().positive(),
        maximumInteriorPages: z.number().int().positive(),
        minimumApprovedMedia: z.number().int().nonnegative(),
        preferredApprovedMedia: z.number().int().nonnegative(),
    })
        .strict(),
    chapterOpeners: z
        .object({ startOnRecto: z.literal(true), suppressRunningFurniture: z.literal(true) })
        .strict(),
    customerVisibleTechnicalLanguage: z.literal("FORBIDDEN"),
    digital: z
        .object({
        searchableText: z.literal(true),
        bookmarks: z.literal(true),
        documentLanguage: z.literal("en-US"),
        cropMarks: z.literal(false),
        taggedReadingOrderRequiredForRelease: z.literal(true),
    })
        .strict(),
    print: z
        .object({
        separateInteriorAndCover: z.literal(true),
        bleedInches: z.literal(0.125),
        minimumImageDpi: z.number().int().min(300),
        binding: z.literal("PERFECT_BOUND"),
        paper: z.literal("60# UNCOATED WHITE"),
        finish: z.literal("MATTE"),
        vendorSubmissionEnabled: z.literal(false),
    })
        .strict(),
})
    .strict()
    .superRefine((value, context) => {
    const expectedFont = value.product === "MY_STORY" ? "Literata" : "Newsreader";
    if (value.body.fontFamily !== expectedFont)
        context.addIssue({
            code: "custom",
            path: ["body", "fontFamily"],
            message: `${value.product} body typography must use ${expectedFont}.`,
        });
    if (value.contentTarget.preferredWords < value.contentTarget.minimumWords)
        context.addIssue({
            code: "custom",
            path: ["contentTarget", "preferredWords"],
            message: "Preferred words cannot be below the minimum.",
        });
    if (value.contentTarget.maximumInteriorPages < value.contentTarget.minimumInteriorPages)
        context.addIssue({
            code: "custom",
            path: ["contentTarget", "maximumInteriorPages"],
            message: "Maximum pages cannot be below the minimum.",
        });
    if (value.contentTarget.preferredApprovedMedia < value.contentTarget.minimumApprovedMedia)
        context.addIssue({
            code: "custom",
            path: ["contentTarget", "preferredApprovedMedia"],
            message: "Preferred media cannot be below the minimum.",
        });
});
export const narrativeLayoutEvidenceSchema = z
    .object({
    contract: z.literal("narrative-layout-evidence/1"),
    product: narrativeBookProductSchema,
    interiorPageCount: z.number().int().positive(),
    chapterStartPages: z.array(z
        .object({
        chapterId: opaqueIdSchema,
        title: z.string().trim().min(1).max(200),
        interiorPage: z.number().int().positive(),
    })
        .strict()),
    contents: z.array(z
        .object({
        title: z.string().trim().min(1).max(200),
        interiorPage: z.number().int().positive(),
    })
        .strict()),
    intentionalBlankPages: z.array(z.number().int().positive()),
    sourceParagraphIds: z.array(z.string().regex(/^[A-Z0-9]+:p\d+$/u)),
    renderedParagraphIds: z.array(z.string().regex(/^[A-Z0-9]+:p\d+$/u)),
    bodyTypography: narrativeBookProductionSpecificationSchema.shape.body,
    customerVisibleTechnicalTerms: z.array(z.string().min(1)).max(0),
})
    .strict()
    .superRefine((value, context) => {
    const expectedFont = value.product === "MY_STORY" ? "Literata" : "Newsreader";
    if (value.bodyTypography.fontFamily !== expectedFont)
        context.addIssue({
            code: "custom",
            path: ["bodyTypography", "fontFamily"],
            message: `${value.product} layout evidence must name ${expectedFont}.`,
        });
    if (value.interiorPageCount % 2 !== 0)
        context.addIssue({
            code: "custom",
            path: ["interiorPageCount"],
            message: "Print interiors must contain an even number of pages.",
        });
    if (value.chapterStartPages.some(({ interiorPage }) => interiorPage % 2 !== 1))
        context.addIssue({
            code: "custom",
            path: ["chapterStartPages"],
            message: "Major chapters must begin on recto pages.",
        });
    if (value.intentionalBlankPages.some((page) => page % 2 !== 0))
        context.addIssue({
            code: "custom",
            path: ["intentionalBlankPages"],
            message: "Inserted blank pages must be verso pages.",
        });
    if (value.sourceParagraphIds.join("|") !== value.renderedParagraphIds.join("|"))
        context.addIssue({
            code: "custom",
            path: ["renderedParagraphIds"],
            message: "Rendered paragraphs must exactly preserve source order and cardinality.",
        });
    if (new Set(value.renderedParagraphIds).size !== value.renderedParagraphIds.length)
        context.addIssue({
            code: "custom",
            path: ["renderedParagraphIds"],
            message: "Rendered paragraph identifiers must be unique.",
        });
    const contents = new Map(value.contents.map(({ title, interiorPage }) => [title, interiorPage]));
    if (value.chapterStartPages.some(({ title, interiorPage }) => contents.get(title) !== interiorPage))
        context.addIssue({
            code: "custom",
            path: ["contents"],
            message: "Every chapter contents destination must equal its planned opening page.",
        });
});
export const narrativeProductionReadinessSchema = z
    .object({
    contract: z.literal("narrative-production-readiness/1"),
    product: narrativeBookProductSchema,
    status: z.enum(["BLOCKED", "READY_FOR_PHYSICAL_PROOF"]),
    technicalPreflight: z.enum(["PASS", "FAIL"]),
    blockers: z.array(z
        .object({
        code: z.enum([
            "CONTENT_DEPTH_BELOW_TARGET",
            "APPROVED_MEDIA_BELOW_TARGET",
            "REPRESENTATIVE_PROOF_APPROVAL_REQUIRED",
            "CATALOG_FORMAT_APPROVAL_REQUIRED",
            "TAGGED_PDF_REQUIRED",
            "PHYSICAL_PROOF_REQUIRED",
        ]),
        detail: z.string().trim().min(1).max(1_000),
    })
        .strict()),
})
    .strict()
    .superRefine((value, context) => {
    if ((value.status === "BLOCKED") !== value.blockers.length > 0)
        context.addIssue({
            code: "custom",
            path: ["status"],
            message: "Blocked status must agree with the presence of release blockers.",
        });
});
//# sourceMappingURL=narrative-book-production.js.map